import * as React from 'react';
import { GroupServiceGetResponse } from '../../../services/api';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import ModalButtons from '../../ui/modals/ModalButtons';
import FileUploader from '../../uploads/FileUploader';
import { APIStatus, GroupStatus } from '../../../utils/types';
import { fetchGroup, submitTier2Request } from '../../../store/group';
import { useAppDispatch, useAppSelector } from '../../../store';
import { canMakeRequests } from '../../../utils/groupList';
import { UserRole } from '../../../store/auth';
import { flattenTimePeriodDate } from '../../../utils/date';

export default function Tier2Request({ group, onActionTier2 }: { group: GroupServiceGetResponse, onActionTier2: () => void }) {
  const [isModalVisible, setIsModalVisible] = React.useState<boolean>(false);
  const [apiState, setApiState] = React.useState<APIStatus | null>(null); 

  const dispatch = useAppDispatch();
  const { role } = useAppSelector((state) => state.auth);
  const [files, setFiles] = React.useState<File[]>([]);
  const [notes, setNotes] = React.useState<string>('');

  const formIsValid = React.useMemo<boolean>(() => {
    let isValid = true;

    if (!notes.length) isValid = false;

    return isValid;
  }, [files, notes]);

  async function submitRequest() {
    try {
      setApiState(APIStatus.LOADING);
      await submitTier2Request({
        file: files[0],
        policyNumber: group.policyDetailModel?.policyNumber!,
        timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
        note: notes,
      });
      setApiState(null);
      hideModal();
      dispatch(fetchGroup({
        policyNumber: group.policyDetailModel?.policyNumber!,
      timePeriodDate: flattenTimePeriodDate(group.policyDetailModel?.timePeriodDate!),
      }));
    } catch {
      setApiState(APIStatus.ERROR);
    }
  }

  function appendFile(files: File[]) {
    setFiles(files);
  }

  function ModalHeader() {
    return (
      <>
        <div className="leading-none">Request tier 2 relief</div>
        <div className="text-lg font-normal">Provide supporting documentation for actuarial review</div>
      </>
    )
  }

  function hideModal() {
    setIsModalVisible(false);
    setNotes('');
    setFiles([]);
  }

  if (!canMakeRequests(group) && group.requestStatus === GroupStatus.TIER2_REQUESTED && role === UserRole.ACTUARY) {
    return (
      <Button severity="secondary" label="action tier 2" rounded onClick={() => onActionTier2()} />
    )
  }

  if (canMakeRequests(group) && role === UserRole.ACTUARY) {
    return <Button severity="secondary" label="action tier 2" rounded onClick={() => onActionTier2()} />
  }

  return (
    <>
      <Button
        rounded
        severity="secondary"
        onClick={() => setIsModalVisible(true)}
        label={group.requestStatus === GroupStatus.TIER2_REQUESTED ? "tier 2 requested" : (role === UserRole.ACTUARY ? "action tier 2" : "request tier 2")}
        disabled={!canMakeRequests(group)}
      />
      <Dialog
        header={ModalHeader}
        visible={isModalVisible}
        onHide={hideModal}
        footer={
          <ModalButtons
            onSave={() => submitRequest()}
            onCancel={() => setIsModalVisible(false)}
            isLoading={apiState === APIStatus.LOADING}
            isFormValid={formIsValid}
          />
        }
      >
        <div className="flex flex-col gap-4">
          <FileUploader files={files} onAddFile={appendFile} />

          <div className="flex flex-col gap-2">
            <p className="font-bold">Supporting notes</p>
            <InputTextarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={6} placeholder="Add supporting notes" />
          </div>
        </div>
      </Dialog>
    </>
  )
}